import type { FC } from "react";

import { Alert, Button, Icon, Modal, Text } from "@alpacahq/ui/old";

import type { Relationship } from "../common/types";
import { getRelationshipInfo } from "./getRelationshipInfo";
import { RelationshipInfo } from "./RelationshipInfo";

type CommissionModelModalProps = {
  isOpen: boolean;
  relationship: Relationship;
  onUnlink: (relationship: Relationship) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

export const UnlinkRelationshipModal: FC<CommissionModelModalProps> = ({
  isOpen,
  relationship,
  onUnlink,
  onCancel,
  isLoading,
}) => {
  const relationshipName =
    relationship.type === "ACH"
      ? relationship?.brand_name
      : relationship.type === "Wire"
        ? relationship.name
        : (relationship.bank_name ?? "this account");

  const isRelationshipRejected =
    relationship.type !== "CurrencyCloud" && relationship.status === "REJECTED";

  const title = isRelationshipRejected
    ? `Remove Rejected ${relationship.type} Connection`
    : `Remove ${relationship.type} Connection`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      controls={
        <div className="flex w-full flex-wrap items-center justify-between">
          <Button
            variant="alternate"
            onClick={() => {
              onCancel();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            onClick={() => onUnlink(relationship)}
            variant="red"
            loading={isLoading}
          >
            Remove
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-8">
        <RelationshipInfo {...getRelationshipInfo(relationship)} />

        <Text>
          {isRelationshipRejected ? (
            <>
              The connection with {relationshipName} has been rejected. In order
              to create a new {relationship.type} connection, you must remove
              this one.
            </>
          ) : (
            <>
              Are you sure you want to remove <b>{relationshipName}</b>?
            </>
          )}
        </Text>

        <Alert
          variant="danger"
          message={
            <span className="flex gap-4">
              <Icon name="ExclamationCircle" />
              <span>
                Any pending transfers using this connection will be canceled due
                to this action.
              </span>
            </span>
          }
          className="mt-0"
        />
      </div>
    </Modal>
  );
};
